import { Button, Card, CardBody, CardFooter, Flex, Image, Stack, Heading, Text, Stat, StatLabel, StatNumber, Link as ChakraLink, useBreakpointValue, Skeleton, SkeletonText } from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';

const SearchHotelCard = ({ hotel, onCardClick }) => {
  const [imageUrl, setImageUrl] = useState('https://maxmypoint.com/default-hotel-image-small.jpg');

  // Check if this is a loading hotel (skeleton)
  const isLoadingHotel = hotel.n && hotel.n.startsWith('Loading');

  useEffect(() => {
    // Use real image if available and not a loading hotel
    if (hotel.i && hotel.i.trim() !== '' && !isLoadingHotel) {
      setImageUrl(hotel.i);
    } else {
      setImageUrl('https://maxmypoint.com/default-hotel-image-small.jpg');
    }
  }, [hotel.i, isLoadingHotel, hotel.id]);

  // For price display - show skeleton if undefined, N/A if 'N/A', or formatted value
  const renderPrice = () => {
    if (hotel.pr === undefined) {
      return <Skeleton height="20px" width="80px" />;
    }
    if (hotel.pr === 'N/A') {
      return 'N/A';
    }
    return `$${Math.round(hotel.pr).toLocaleString()}`;
  };

  // For points display - show skeleton if undefined, N/A if 'N/A', or formatted value
  const renderPoints = () => {
    if (hotel.pt === undefined) {
      return <Skeleton height="20px" width="60px" />;
    }
    if (hotel.pt === 'N/A') {
      return 'N/A';
    }
    // Only format if hotel.pt is a valid number
    if (typeof hotel.pt === 'number' && !isNaN(hotel.pt)) {
      return `${(hotel.pt / 1000).toFixed(1)}K`;
    }
    return 'N/A';
  };

  // For cents per point display - show skeleton if undefined, N/A if 'N/A', or formatted value
  const renderCentsPerPoint = () => {
    if (hotel.v === undefined) {
      return <Skeleton height="20px" width="40px" />;
    }
    if (hotel.v === 'N/A') {
      return 'N/A';
    }
    // Only call toFixed if hotel.v is a number
    if (typeof hotel.v === 'number' && !isNaN(hotel.v)) {
      return hotel.v.toFixed(1);
    }
    return 'N/A';
  };

  const handleCardClick = (e) => {
    // Don't trigger if clicking on a link or button
    if (e.target.tagName === 'A' || e.target.closest('a') || e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      return;
    }
    if (onCardClick && !isLoadingHotel) {
      onCardClick();
    }
  };

  return (
    <Card
      direction={['column', 'row']} 
      overflow='hidden'
      variant='outline'
      borderColor='gray.300'
      onClick={handleCardClick}
      cursor={!isLoadingHotel ? 'pointer' : 'default'}
      _hover={!isLoadingHotel ? { borderColor: 'blue.400', boxShadow: 'md' } : {}}
    >
      <Image
        objectFit='cover'
        maxW={{base: '100%', md: '200px'}}
        maxH={{ base: '150px', md: '250px' }}
        src={imageUrl}
        alt={hotel.n || 'Hotel'}
        onError={() => setImageUrl('https://maxmypoint.com/default-hotel-image-small.jpg')}
      />
      <Stack width='100%'>
        <CardBody width='100%'>
          <Stack direction='column' gap={3} width='100%'>
            <Heading size='md'>
              {isLoadingHotel ? (
                <SkeletonText noOfLines={1} spacing='4' skeletonHeight='2' />
              ) : (
                <ChakraLink as={RouterLink} to={`https://maxmypoint.com/hotel/${hotel.id}`} target='_blank'>
                  {hotel.n}
                </ChakraLink>
              )}
            </Heading>
            <Flex direction='row'>
              <Stat width='50%'>
                <StatNumber color={hotel.pr === 'N/A' ? 'gray' : ''}>{renderPrice()}</StatNumber>
                <StatLabel color={hotel.pr === 'N/A' ? 'gray' : ''}>Total Price</StatLabel>
              </Stat>
              <Stat width='50%'>
                <StatNumber color={hotel.pt === 'N/A' ? 'gray' : ''}>{renderPoints()}</StatNumber>
                <StatLabel color={hotel.pt === 'N/A' ? 'gray' : ''}>Total Points</StatLabel>
              </Stat>
              <Stat width='50%'>
                <StatNumber color={hotel.v === 'N/A' ? 'gray' : ''}>{renderCentsPerPoint()}</StatNumber>
                <StatLabel color={hotel.v === 'N/A' ? 'gray' : ''}>
                  {useBreakpointValue({ base: 'CPP', md: 'Cents Per Point' })}
                </StatLabel>
              </Stat>
            </Flex>
          </Stack>
        </CardBody>
        <CardFooter pt={0} gap={5}>
          {isLoadingHotel ? (
            <Skeleton height="40px" width="200px" />
          ) : (
            <ChakraLink
              as={RouterLink}
              to={`https://maxmypoint.com/hotel/${hotel.id}`}
              target='_blank'
            >
              <Button variant='solid' colorScheme='blue'>
                View Calendar & Setup Alert
              </Button>
            </ChakraLink>
          )}
        </CardFooter>
      </Stack>
    </Card>
  );
};

export default SearchHotelCard;
